import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { ProductSupplierOrderChannel } from '@gooduncles/gu-app-schema';
import { Button, DatePicker, Select, Tabs } from 'antd';
import Checkbox, { CheckboxChangeEvent } from 'antd/es/checkbox';
import dayjs from 'dayjs';
import { partition } from 'lodash-es';
import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { combineLatest } from 'rxjs';
import { orderChannelOptions } from 'src/constants';

import { formatDate, getDefaultDate } from 'src/utils/date-util';
import { observeProductSupplier, observeProductSupplierPurchaseOrder } from 'src/utils/firebase/firebase-short-cut';
import {
  PurchaseOrderWithProductSupplier,
  convertPurchaseOrderToSMSPostRequest,
  getMessageWrapper,
} from 'src/utils/supplier-purchase-order-util';

import { useCommerceConf } from 'src/redux/hooks/useCommerceConf';

import { useTitle } from 'src/hooks/useTitle';

import SendPurchaseOrderSmsModal from 'src/atomic-components/organisms/Modals/SendPurchaseOrderSmsModal/SendPurchaseOrderSmsModal';
import SupplierPurchaseOrderPrint from 'src/atomic-components/organisms/Tables/SupplierPurchaseOrderPrint/SupplierPurchaseOrderPrint';
import SupplierPurchaseOrderTable from 'src/atomic-components/organisms/Tables/SupplierPurchaseOrderTable';

const defaultValue = getDefaultDate(1);

const PurchaseOrderDashboard: FC = () => {
  useTitle('발주 관리 | 발주서');
  const navigate = useNavigate();
  const { tab } = useParams();
  const { getDeliveryDate, commerceConf } = useCommerceConf();

  const messageWrapper = useMemo(() => {
    const date = getDeliveryDate();
    return getMessageWrapper(date, commerceConf?.purchaseOrderInquiryNumber);
  }, [commerceConf?.purchaseOrderInquiryNumber, getDeliveryDate]);

  const [rowData, setRowData] = useState<PurchaseOrderWithProductSupplier[]>([]);
  const [showHeader, setShowHeader] = useState(false);
  const [purchaseChannels, setPurchaseChannels] = useState<ProductSupplierOrderChannel[]>([
    ProductSupplierOrderChannel.KAKAOTALK,
    ProductSupplierOrderChannel.SMS,
  ]);
  const [date, setDate] = useState<dayjs.Dayjs | null>(defaultValue);
  const disableAddADay = useMemo(() => (date?.unix() ? date?.unix() >= Date.now() / 1000 : false), [date]);
  const dateForWhere = useMemo(() => formatDate(date ? date?.toDate() : new Date(), 'yyyy-MM-dd'), [date]);

  const [selectedRows, setSelectedRows] = useState<PurchaseOrderWithProductSupplier[]>([]);
  const { smsRequests, failedOrderIds } = useMemo(() => {
    const result = selectedRows.map((row) => convertPurchaseOrderToSMSPostRequest(row, messageWrapper));

    const [stringResults, smsRequests] = partition(result, (item): item is string => typeof item === 'string');
    return { smsRequests, failedOrderIds: stringResults };
  }, [selectedRows, messageWrapper]);

  const addADay = () => setDate((prev) => (prev ? prev.add(1, 'day') : null));
  const subADay = () => setDate((prev) => (prev ? prev.subtract(1, 'day') : null));
  const onChangeShowHeader = (e: CheckboxChangeEvent) => setShowHeader(e.target.checked);
  const onPurchaseMethodsChange = (value: ProductSupplierOrderChannel[]) => setPurchaseChannels(value);

  const onTabChange = (key: string) => {
    navigate(`../dashboard/${key}`);
  };

  useEffect(() => {
    if (!dateForWhere) {
      return;
    }

    const productSupplierObserver = observeProductSupplier([['deletedAt', '==', null]]);
    const purchaseOrderObserver = observeProductSupplierPurchaseOrder([['orderDate', '==', dateForWhere]]);

    const subscription = combineLatest([productSupplierObserver, purchaseOrderObserver]).subscribe(
      ([productSuppliers, purchaseOrders]) => {
        setRowData(
          purchaseOrders.map((purchaseOrder) => ({
            ...purchaseOrder,
            productSupplier: productSuppliers.find(
              (productSupplier) => productSupplier._id === purchaseOrder.supplierId
            ),
          }))
        );
      }
    );
    return () => {
      subscription.unsubscribe();
    };
  }, [dateForWhere]);

  return (
    <div className='flexColumn height100'>
      <div className='flexRow flexAlignCenter flexSpaceBetween'>
        <div className='flexRow flexAlignCenter'>
          <Button icon={<LeftOutlined />} onClick={subADay} />
          <DatePicker onChange={setDate} defaultValue={defaultValue} value={date} />
          <Button icon={<RightOutlined />} onClick={addADay} disabled={disableAddADay} />
          {tab === 'table' && (
            <Checkbox checked={showHeader} onChange={onChangeShowHeader}>
              머릿말 표시
            </Checkbox>
          )}
        </div>
        {tab === 'table' && <SendPurchaseOrderSmsModal requests={smsRequests} failedOrderIds={failedOrderIds} />}
        {tab === 'print' && (
          <Select
            mode='multiple'
            allowClear
            style={{ minWidth: 200 }}
            placeholder='출력할 발주방식 선택'
            defaultValue={purchaseChannels}
            onChange={onPurchaseMethodsChange}
            options={orderChannelOptions}
          />
        )}
      </div>
      <Tabs
        type='card'
        accessKey={tab}
        style={{ height: '100%', overflow: 'scroll' }}
        tabBarStyle={{ marginBottom: 0 }}
        onChange={onTabChange}
        items={[
          {
            key: 'table',
            label: '기본 보기',
            children: (
              <SupplierPurchaseOrderTable
                rowData={rowData}
                setSelectedRows={setSelectedRows}
                showHeader={showHeader}
                messageWrapper={messageWrapper}
              />
            ),
          },
          {
            key: 'print',
            label: '출력용 보기',
            children: <SupplierPurchaseOrderPrint rowData={rowData} selectedChannels={purchaseChannels} />,
          },
        ]}
      />
    </div>
  );
};

export default PurchaseOrderDashboard;
